import React, { useState } from "react";
import { Link } from "react-router-dom";
import MapComponent from "./MapComponent";

const DemoProjectDetails = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [projectData, setProjectData] = useState({
    title: "Munich Urban Colab Construction Site",
    status: "Processing Complete",
    location: "Freddy Mercury Str. 5, Munich, Germany",
    coordinates: { lat: 48.1351, lng: 11.582 },
    lastUpdated: new Date().toISOString(),
    description:
      "Demo project created from 360° video footage, processed using AI to generate 3D model.",
    metrics: {
      progress: 12,
      area: "2,450 m²",
      height: "32.5 m",
      estimatedCompletion: "2024-12-31",
    },
    processingDetails: {
      videoLength: "4:32 minutes",
      framesCaptured: "1,630",
      pointsGenerated: "12.5M",
      processingTime: "15 minutes",
    },
  });

  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const sampleFile = {
    name: "sample-construction-footage.mp4",
    size: 128000000, // 128MB
    type: "video/mp4",
  };

  const handleNewStateUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Simulate processing and update the project state
      setProjectData((prev) => ({
        ...prev,
        lastUpdated: new Date().toISOString(),
        processingDetails: {
          ...prev.processingDetails,
          videoLength: "5:00 minutes", // Example new data
          framesCaptured: "1,800",
          pointsGenerated: "13.0M",
          processingTime: "20 minutes",
        },
      }));
      alert(`New video state uploaded: ${file.name}`);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      simulateUpload(file);
    }
  };

  const handleUseSampleFile = () => {
    simulateUpload(sampleFile);
  };

  const simulateUpload = (file) => {
    setLoading(true);
    let progress = 0;
    const interval = setInterval(() => {
      progress += 5;
      setUploadProgress(progress);
      if (progress >= 100) {
        clearInterval(interval);
        setLoading(false);
        setShowUploadDialog(false);
        // Update project data with new state
        setProjectData((prev) => ({
          ...prev,
          lastUpdated: new Date().toISOString(),
          processingDetails: {
            ...prev.processingDetails,
            videoLength: "5:00 minutes",
            framesCaptured: "1,800",
            pointsGenerated: "13.0M",
            processingTime: "20 minutes",
          },
        }));
      }
    }, 200);
  };

  const [discrepancyReports] = useState([
    {
      id: 1,
      date: "2024-03-15",
      title: "March Progress Discrepancy Report",
      fileSize: "2.4 MB",
      status: "Critical",
    },
    {
      id: 2,
      date: "2024-03-01",
      title: "Early March Alignment Analysis",
      fileSize: "1.8 MB",
      status: "Minor",
    },
    {
      id: 3,
      date: "2024-02-15",
      title: "February Progress Discrepancy Report",
      fileSize: "2.1 MB",
      status: "Moderate",
    },
    {
      id: 4,
      date: "2024-02-01",
      title: "Early February Alignment Analysis",
      fileSize: "1.9 MB",
      status: "Minor",
    },
  ]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {projectData.title}
            </h1>
            <p className="text-gray-500 mt-1">
              Last updated: {new Date(projectData.lastUpdated).toLocaleString()}
            </p>
          </div>
          <span className="px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            {projectData.status}
          </span>
        </div>
      </div>

      {/* Upload New State Button */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col space-y-4">
          <div className="flex justify-end space-x-4">
            <button
              onClick={() => alert("Upload Project Plan clicked")}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 cursor-pointer"
            >
              Upload Project Plan
            </button>
            <button
              onClick={() => setShowUploadDialog(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 cursor-pointer"
            >
              Upload New State
            </button>
          </div>

          <div className="border-t pt-4">
            <p className="text-sm text-gray-500 mb-3">
              Connect with external platforms:
            </p>
            <div className="flex space-x-4">
              <button
                onClick={() => alert("Connect Procore Account clicked")}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Connect Procore
              </button>

              <button
                onClick={() => alert("Connect BIM 360 Account clicked")}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Connect BIM 360
              </button>

              <button
                onClick={() => alert("Connect ACC Account clicked")}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Connect ACC
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Dialog */}
      {showUploadDialog && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium">Upload New State</h3>
              <button
                onClick={() => setShowUploadDialog(false)}
                className="text-gray-400 hover:text-gray-500"
              >
                <svg
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {!loading && (
              <>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center">
                  <input
                    type="file"
                    accept="video/*"
                    className="hidden"
                    id="video-upload"
                    onChange={handleFileUpload}
                  />
                  <label htmlFor="video-upload" className="cursor-pointer">
                    <div className="space-y-4">
                      <div className="mx-auto h-12 w-12 text-gray-400">
                        <svg
                          className="h-12 w-12"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <div className="text-gray-600">
                        <span className="text-blue-600 hover:text-blue-500">
                          Upload a video
                        </span>{" "}
                        or drag and drop
                      </div>
                      <p className="text-xs text-gray-500">
                        MP4, MOV up to 2GB
                      </p>
                    </div>
                  </label>
                </div>

                <div className="relative my-4">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">Or</span>
                  </div>
                </div>

                <div className="text-center">
                  <button
                    onClick={handleUseSampleFile}
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Use Sample File
                  </button>
                </div>
              </>
            )}

            {loading && (
              <div className="space-y-4">
                <div className="flex justify-between text-sm text-gray-600">
                  <span>Uploading...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {[
              "overview",
              "processing",
              "3d-model",
              "project-plan",
              "plan-discrepancies",
            ].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 px-6 text-sm font-medium border-b-2 ${
                  activeTab === tab
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }`}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1).replace("-", " ")}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === "overview" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Project Info */}
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    Project Details
                  </h3>
                  <p className="mt-2 text-gray-600">
                    {projectData.description}
                  </p>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-900">
                    Key Metrics
                  </h4>
                  <dl className="mt-2 grid grid-cols-2 gap-4">
                    {Object.entries(projectData.metrics).map(([key, value]) => (
                      <div key={key} className="bg-gray-50 p-3 rounded-lg">
                        <dt className="text-sm font-medium text-gray-500 capitalize">
                          {key.replace(/([A-Z])/g, " $1").trim()}
                        </dt>
                        <dd className="mt-1 text-sm font-semibold text-gray-900">
                          {value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>

              {/* Map */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">Location</h3>
                <div className="h-64 rounded-lg overflow-hidden">
                  <MapComponent location={projectData.coordinates} />
                </div>
                <p className="text-sm text-gray-600">{projectData.location}</p>
              </div>
            </div>
          )}

          {activeTab === "processing" && (
            <div className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg
                      className="h-5 w-5 text-blue-400"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-blue-800">
                      Processing Complete
                    </h3>
                    <p className="text-sm text-blue-700 mt-1">
                      Your video has been successfully processed and converted
                      into a 3D model.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                {Object.entries(projectData.processingDetails).map(
                  ([key, value]) => (
                    <div key={key} className="bg-gray-50 rounded-lg p-4">
                      <dt className="text-sm font-medium text-gray-500 capitalize">
                        {key.replace(/([A-Z])/g, " $1").trim()}
                      </dt>
                      <dd className="mt-1 text-lg font-semibold text-gray-900">
                        {value}
                      </dd>
                    </div>
                  )
                )}
              </div>

              <div className="mt-6">
                <Link
                  to="3d-model"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                >
                  View 3D Model
                </Link>
              </div>
            </div>
          )}

          {activeTab === "3d-model" && (
            <div className="text-center py-12">
              <div className="mx-auto w-24 h-24 text-gray-400">
                <svg
                  className="w-full h-full"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                  />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                3D Model Viewer
              </h3>
              <p className="mt-2 text-gray-500">
                The 3D model is ready to view. Click the button below to open
                the viewer.
              </p>
              <div className="mt-6">
                <Link
                  to="3d-model"
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                >
                  Open 3D Viewer
                </Link>
              </div>
            </div>
          )}

          {activeTab === "project-plan" && (
            <div className="text-center py-12">
              <div className="mx-auto w-24 h-24 text-gray-400">
                <svg
                  className="w-full h-full"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <h3 className="mt-4 text-lg font-medium text-gray-900">
                Project Plan Overview
              </h3>
              <p className="mt-2 text-gray-500">
                View and manage your project planning details here.
              </p>
              <div className="mt-6">
                <button
                  onClick={() => alert("View Project Plan clicked")}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                >
                  View Project Plan
                </button>
              </div>
            </div>
          )}

          {activeTab === "plan-discrepancies" && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-medium text-gray-900">
                  Plan Discrepancy Reports
                </h3>
                <button
                  onClick={() => alert("Generate new report")}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
                >
                  Generate New Report
                </button>
              </div>

              <div className="bg-white shadow overflow-hidden sm:rounded-md">
                <ul className="divide-y divide-gray-200">
                  {discrepancyReports.map((report) => (
                    <li key={report.id}>
                      <div className="px-4 py-4 sm:px-6 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex flex-col">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {report.title}
                            </p>
                            <p className="text-sm text-gray-500">
                              Generated on{" "}
                              {new Date(report.date).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center space-x-4">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                                ${
                                  report.status === "Critical"
                                    ? "bg-red-100 text-red-800"
                                    : report.status === "Moderate"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : "bg-green-100 text-green-800"
                                }`}
                            >
                              {report.status}
                            </span>
                            <span className="text-sm text-gray-500">
                              {report.fileSize}
                            </span>
                            <button
                              onClick={() =>
                                alert(`Downloading: ${report.title}`)
                              }
                              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              <svg
                                className="h-4 w-4 mr-1"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                />
                              </svg>
                              Download
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gray-50 px-4 py-3 text-sm text-gray-500 rounded-md">
                <p>
                  Reports are automatically generated when significant
                  discrepancies are detected between the project plan and actual
                  progress.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="text-sm font-medium text-gray-900">
              Overall Progress
            </span>
            <span className="text-sm font-medium text-gray-900">
              {projectData.metrics.progress}%
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-500"
              style={{ width: `${projectData.metrics.progress}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DemoProjectDetails;
